import { makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState } from "react";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { TooltipProps } from "@material-ui/core/Tooltip";
import { tesaBlue, tesaGrey2 } from "../styles/colors";
import StyledTooltip from "./Wrapper/StyledTooltip";

interface InfoBoxProps {
  alignTop?: boolean;
  className?: string;
  compress?: boolean;
  flexChildren?: boolean;
  flipChildren?: boolean;
  hidden?: boolean;
  // change type to JSX.Element to accept pure nodes as prop instead (quick solution for passing clickable links)
  text: string;
  TooltipProps?: Partial<TooltipProps>;
}

const useStyles = makeStyles(() => ({
  root: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    justifyContent: (props) => (props.flipChildren ? "flex-end" : ""),
    width: "inherit",
  },
  children: (props) => {
    const { compress, flexChildren, flipChildren } = props;
    return {
      alignItems: flexChildren ? "center" : undefined,
      display: flexChildren ? "flex" : undefined,
      flexGrow: compress ? 0 : 1,
      marginLeft: flipChildren ? "12px" : "",
    };
  },
  container: {
    alignSelf: (props) => (props.alignTop ? "flex-start" : ""),
    height: "16px",
    marginLeft: (props) => (props.flipChildren ? "" : "12px"),
    marginRight: (props) => (props.flipChildren ? "12px" : ""),
    transform: (props: InfoBoxProps) => (props.alignTop ? "translateY(18px)" : ""),
    width: "16px",
  },
  infoIcon: {
    cursor: "pointer",
    height: "inherit",
    outline: "none",
    width: "inherit",
  },
}));

const InfoBox: React.FC<InfoBoxProps> = (props) => {
  const { children, className, flipChildren, hidden, text, TooltipProps } = props;
  const [boxVisible, setBoxVisible] = useState(false);
  const [keepBoxVisible, setKeepBoxVisible] = useState(false);
  const [titleNode, setTitleNode] = useState<JSX.Element>();
  const classes = useStyles(props);

  const app = document.getElementById("root");
  const dialogContainer = document.getElementsByClassName("MuiDialog-container");

  useEffect(() => {
    const clickEvent = () => setKeepBoxVisible(false);
    app?.addEventListener("click", clickEvent);

    if (dialogContainer && dialogContainer.length > 0) {
      dialogContainer[0].addEventListener("click", clickEvent);
    }

    // use * to type lists with the text prop
    if (text.includes("*")) {
      const node = (
        <ul style={{ paddingLeft: "16px" }}>
          {text
            .split("*")
            .map((textLine) => textLine.trim())
            .filter((textLine) => Boolean(textLine))
            .map((textLine, i) => (
              <li key={textLine + String(i)}>{textLine}</li>
            ))}
        </ul>
      );
      setTitleNode(node);
    } else {
      setTitleNode(undefined);
    }

    return () => {
      app?.removeEventListener("click", () => clickEvent);
      if (dialogContainer && dialogContainer.length > 0) {
        dialogContainer[0].removeEventListener("click", clickEvent);
      }
    };
  }, [text]);

  return (
    <div className={[className, classes.root].join(" ")}>
      {!flipChildren && <span className={classes.children}>{children}</span>}
      <span className={classes.container}>
        <StyledTooltip
          {...(TooltipProps || {})}
          title={titleNode || text}
          open={boxVisible || keepBoxVisible}
          interactive
        >
          <InfoOutlinedIcon
            onClick={() => setKeepBoxVisible(!keepBoxVisible)}
            onMouseOver={() => setBoxVisible(true)}
            onFocus={() => setBoxVisible(true)}
            onMouseLeave={() => setBoxVisible(false)}
            onBlur={() => setBoxVisible(false)}
            role="button"
            tabIndex={0}
            className={classes.infoIcon}
            style={{
              color: boxVisible || keepBoxVisible ? tesaBlue : tesaGrey2,
              display: hidden ? "none" : "block",
            }}
          />
        </StyledTooltip>
      </span>
      {flipChildren && <span className={classes.children}>{children}</span>}
    </div>
  );
};

export default InfoBox;
